import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Plus } from "lucide-react";
import { CHARTS } from "../../data/products";

const FEEDS = [
  { label: "Calf starter & pre-weaning", codes: ["101", "301", "302"] },
  { label: "Dairy concentrate", codes: ["201", "302", "650"] },
  { label: "TMR, mineral & straw rations", codes: ["401", "650"] },
  { label: "Heifer & young stock", codes: ["101", "302"] },
];

const pref = (code) => CHARTS.preference.find((p) => p.code === code)?.palativa ?? 0;

const ROWS = [
  ["Sensory notes", (v) => v.notes],
  ["Best for", (v) => v.best],
  ["Inclusion", (v) => <span className="font-mono">{v.dose}</span>],
];

const PrefBar = ({ v, accent }) => (
  <div>
    <div className="h-2.5 rounded-full bg-[#E2E8E3] overflow-hidden">
      <motion.div initial={{ width: 0 }} whileInView={{ width: `${pref(v.code)}%` }} viewport={{ once: true }} transition={{ duration: 0.9 }} className="h-full rounded-full" style={{ background: accent }} />
    </div>
    <p className="font-mono text-sm mt-1.5">{pref(v.code)}% of intake*</p>
  </div>
);

const Head = ({ v, accent }) => (
  <div className="flex items-center gap-3">
    <span className="h-10 w-10 rounded-full border border-black/10 shrink-0" style={{ background: v.swatch }} />
    <div>
      <p className="font-display text-2xl leading-none" style={{ color: accent }}>{v.code}</p>
      <p className="font-display text-base text-[var(--forest)] mt-1">{v.name}</p>
    </div>
  </div>
);

export const FlavourCompare = ({ r }) => {
  const [sel, setSel] = useState(["101", "201", "301"]);
  const [feed, setFeed] = useState(null);
  const chosen = r.variants.filter((v) => sel.includes(v.code));

  const toggle = (code) => {
    setFeed(null);
    setSel((s) => (s.includes(code) ? (s.length > 1 ? s.filter((c) => c !== code) : s) : [...s, code].slice(-3)));
  };
  const pickFeed = (f) => { setFeed(f.label); setSel(f.codes); };

  return (
    <section id="compare" data-testid="flavour-compare" className="bg-[var(--paper)] border-y border-[var(--line)]">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-20 md:py-28">
        <p className="text-[13px] font-bold uppercase tracking-[0.2em] text-[var(--leaf)]">Compare flavours</p>
        <h2 className="font-display text-3xl md:text-5xl text-[var(--forest)] mt-4">Find the right PALATIVA for your feed.</h2>
        <p className="text-[var(--muted)] mt-4 max-w-2xl">Start with your feed type, or pick up to three flavours to compare side by side.</p>

        <div className="mt-10 grid lg:grid-cols-2 gap-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--muted)] mb-3">1 · My feed is</p>
            <div className="flex flex-wrap gap-2">
              {FEEDS.map((f) => (
                <button key={f.label} data-testid={`compare-feed-${f.codes.join("-")}`} onClick={() => pickFeed(f)} className={`rounded-md border px-4 py-2.5 text-sm font-semibold transition-colors ${feed === f.label ? "bg-[var(--forest)] text-white border-[var(--forest)]" : "bg-white border-[var(--line)] hover:border-[var(--forest)]"}`}>{f.label}</button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--muted)] mb-3">2 · Flavours to compare ({sel.length}/3)</p>
            <div className="flex flex-wrap gap-2">
              {r.variants.map((v) => {
                const on = sel.includes(v.code);
                return (
                  <button key={v.code} data-testid={`compare-toggle-${v.code}`} onClick={() => toggle(v.code)} className={`inline-flex items-center gap-2 rounded-md border px-3.5 py-2.5 text-sm font-semibold transition-colors ${on ? "bg-white border-[var(--forest)] text-[var(--forest)]" : "bg-white border-[var(--line)] text-[var(--muted)] hover:border-[var(--forest)]"}`}>
                    <span className="h-3.5 w-3.5 rounded-full border border-black/10" style={{ background: v.swatch }} />
                    {v.code}
                    {on ? <Check size={14} className="text-[var(--leaf)]" /> : <Plus size={14} />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-10 hidden md:block bg-white rounded-[10px] border border-[var(--line)] overflow-hidden" data-testid="compare-table">
          <table className="w-full table-fixed text-left">
            <thead>
              <tr className="border-b border-[var(--line)]">
                <th className="w-48 p-6" />
                {chosen.map((v) => <th key={v.code} className="p-6 align-top"><Head v={v} accent={r.accent} /></th>)}
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([label, get]) => (
                <tr key={label} className="border-b border-[var(--line)]">
                  <td className="p-6 text-xs font-bold uppercase tracking-widest text-[var(--muted)] align-top">{label}</td>
                  {chosen.map((v) => <td key={v.code} className="p-6 align-top text-[var(--ink)]">{get(v)}</td>)}
                </tr>
              ))}
              <tr>
                <td className="p-6 text-xs font-bold uppercase tracking-widest text-[var(--muted)] align-top">Two-choice preference</td>
                {chosen.map((v) => <td key={v.code} className="p-6 align-top"><PrefBar v={v} accent={r.accent} /></td>)}
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-8 md:hidden space-y-4" data-testid="compare-cards">
          {chosen.map((v) => (
            <div key={v.code} className="bg-white rounded-[10px] border border-[var(--line)] p-5">
              <Head v={v} accent={r.accent} />
              <dl className="mt-4 space-y-3">
                {ROWS.map(([label, get]) => (
                  <div key={label}>
                    <dt className="text-[11px] font-bold uppercase tracking-widest text-[var(--muted)]">{label}</dt>
                    <dd className="text-[var(--ink)] text-sm mt-0.5">{get(v)}</dd>
                  </div>
                ))}
                <div>
                  <dt className="text-[11px] font-bold uppercase tracking-widest text-[var(--muted)] mb-1">Two-choice preference</dt>
                  <PrefBar v={v} accent={r.accent} />
                </div>
              </dl>
            </div>
          ))}
        </div>
        <p className="mt-5 text-xs text-[var(--muted)]">*Share of total intake in indicative two-choice tests vs control feed (50% = no preference). Feed-type suggestions follow each flavour's recommended use.</p>
      </div>
    </section>
  );
};
